(() => {
  const selectors = {
    cartDrawer: 'cart-drawer',
    cartCount: '[data-cart-count]',
    productForm: 'form[action*="/cart/add"]',
  };

  const escapeHTML = (value = '') => {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  };

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const parseMoneyText = (value) => {
    const numeric = String(value || '').replace(/[^0-9.]/g, '');
    return Number(numeric);
  };

  const autoDeliveryLabel = (planName = '') => {
    const frequency = String(planName).match(/every\s+\d+\s+(?:day|week|month|year)s?/i)?.[0];

    if (frequency) {
      return `Auto delivers ${frequency.toLowerCase()}`;
    }

    const cleanedName = String(planName).replace(/^deliver(?:y)?\s*/i, '').trim();
    return cleanedName ? `Auto delivers ${cleanedName}` : 'Auto delivers on schedule';
  };

  const createBundleId = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `bundle-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const cartJson = async () => {
    const response = await fetch('/cart.js', {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart');
    }

    return response.json();
  };

  const updateCartLines = async (updates) => {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error('Unable to update cart');
    }

    return response.json();
  };

  const removeBundle = async (bundleId, cart) => {
    const currentCart = cart || (await cartJson());
    const updates = {};

    currentCart.items.forEach((item) => {
      if (item.properties?._bundle_id === bundleId) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      return currentCart;
    }

    return updateCartLines(updates);
  };

  const removeVariantLines = async (variantId) => {
    const currentCart = await cartJson();
    const updates = {};

    currentCart.items.forEach((item) => {
      if (String(item.variant_id) === String(variantId)) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      return currentCart;
    }

    return updateCartLines(updates);
  };

  const bundleDiscountApplied = (cart, bundleId, paidQuantity) => {
    const bundleItems = cart.items.filter((item) => item.properties?._bundle_id === bundleId);
    const bonusItem = bundleItems.find((item) => item.properties?._bundle_role === 'bonus');

    if (bundleItems.length < 2 || !bonusItem || Number(bonusItem.final_line_price) !== 0) {
      return false;
    }

    const unitPrice = Math.max(...bundleItems.map((item) => Number(item.original_price || 0)));
    const bundleTotal = bundleItems.reduce((total, item) => total + Number(item.final_line_price || 0), 0);

    return unitPrice > 0 && bundleTotal <= unitPrice * paidQuantity;
  };

  const getCarouselScroller = (carousel) => {
    const viewport = carousel.querySelector('[data-carousel-viewport]');
    return viewport?.firstElementChild || viewport;
  };

  const updateCarouselCounter = (carousel) => {
    const scroller = getCarouselScroller(carousel);
    const counter = carousel.querySelector('[data-carousel-counter]');

    if (!scroller || !counter || !scroller.children.length) {
      return;
    }

    const firstSlide = scroller.children[0];
    const step = firstSlide.getBoundingClientRect().width || scroller.clientWidth || 1;
    const index = Math.min(scroller.children.length, Math.round(scroller.scrollLeft / step) + 1);

    counter.textContent = `${index} / ${scroller.children.length}`;
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.cart-drawer__panel');
      this.body = this.querySelector('[data-cart-drawer-body]');
      this.footer = this.querySelector('[data-cart-footer]');
      this.subtotal = this.querySelector('[data-cart-subtotal]');

      document.querySelectorAll('[data-cart-open]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.open());
      });

      this.querySelectorAll('[data-cart-close]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.close());
      });

      this.addEventListener('click', (event) => {
        const bundleRemoveButton = event.target.closest('[data-cart-remove-bundle]');

        if (bundleRemoveButton) {
          bundleRemoveButton.setAttribute('aria-busy', 'true');
          this.removeBundle(bundleRemoveButton.dataset.bundleId).finally(() => {
            bundleRemoveButton.removeAttribute('aria-busy');
          });
          return;
        }

        const quantityButton = event.target.closest('[data-cart-quantity]');

        if (!quantityButton) {
          return;
        }

        const line = quantityButton.closest('[data-cart-line]');
        const quantity = Number(line?.dataset.quantity || line?.querySelector('[data-cart-current-quantity]')?.textContent || 0);
        const nextQuantity = Math.max(0, quantity + Number(quantityButton.dataset.cartQuantity));

        this.changeLine(quantityButton.dataset.lineKey, nextQuantity);
      });

      document.addEventListener('cart:refresh', () => this.refresh());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    open(cart) {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';

      if (cart) {
        this.render(cart);
      } else {
        this.refresh();
      }

      window.setTimeout(() => {
        this.panel?.focus();
      }, 50);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    }

    async refresh() {
      try {
        const cart = await cartJson();
        this.render(cart);
      } catch (error) {
        // Keep the server-rendered cart in place if the cart API is unavailable.
      }
    }

    async removeBundle(bundleId) {
      if (!bundleId) {
        return;
      }

      try {
        const cart = await removeBundle(bundleId);
        this.render(cart);
      } catch (error) {
        this.refresh();
      }
    }

    async changeLine(key, quantity) {
      if (!key) {
        return;
      }

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: key, quantity }),
        });

        if (!response.ok) {
          throw new Error('Unable to update cart');
        }

        this.render(await response.json());
      } catch (error) {
        this.refresh();
      }
    }

    render(cart) {
      document.querySelectorAll(selectors.cartCount).forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });

      if (this.footer) {
        this.footer.hidden = cart.item_count === 0;
      }

      if (this.subtotal) {
        this.subtotal.textContent = formatMoney(cart.total_price);
      }

      if (!this.body) {
        return;
      }

      if (!cart.items.length) {
        this.body.innerHTML = `
          <div class="cart-drawer__empty">
            <h3>Your cart is empty</h3>
            <div class="rte"><p>Add products to your cart to check out.</p></div>
            <a class="button" href="/collections/all">Shop products</a>
          </div>
        `;
        return;
      }

      this.body.innerHTML = `
        <div class="cart-drawer__items">
          ${this.orderedItems(cart.items)
            .map((item) => this.renderItem(item, this.bundlePresentation(cart.items, item)))
            .join('')}
        </div>
      `;
    }

    orderedItems(items) {
      const ordered = [];
      const renderedBundles = new Set();

      items.forEach((item) => {
        const bundleId = item.properties?._bundle_id;

        if (!bundleId) {
          ordered.push(item);
          return;
        }

        if (renderedBundles.has(bundleId)) {
          return;
        }

        const bundleItems = items.filter((candidate) => candidate.properties?._bundle_id === bundleId);
        bundleItems.sort((left, right) => {
          const roleOrder = { paid: 0, bonus: 1 };
          return (roleOrder[left.properties?._bundle_role] ?? 2) - (roleOrder[right.properties?._bundle_role] ?? 2);
        });

        ordered.push(...bundleItems);
        renderedBundles.add(bundleId);
      });

      return ordered;
    }

    bundlePresentation(items, item) {
      const bundleId = item.properties?._bundle_id;

      if (!bundleId) {
        return null;
      }

      const bundleItems = items.filter((candidate) => candidate.properties?._bundle_id === bundleId);
      const paidItem = bundleItems.find((candidate) => candidate.properties?._bundle_role === 'paid');
      const bonusItem = bundleItems.find((candidate) => candidate.properties?._bundle_role === 'bonus');

      if (!paidItem || !bonusItem) {
        return null;
      }

      const paidQuantity = Number(paidItem.properties?._bundle_paid_quantity || paidItem.quantity);
      const bonusQuantity = Number(bonusItem.properties?._bundle_bonus_quantity || bonusItem.quantity);
      const unitPrice = Math.max(...bundleItems.map((candidate) => Number(candidate.original_price || 0)));
      const bundleTotal = bundleItems.reduce((total, candidate) => total + Number(candidate.final_line_price || 0), 0);

      if (!unitPrice || bundleTotal > unitPrice * paidQuantity) {
        return null;
      }

      if (item.key === paidItem.key) {
        return {
          originalLinePrice: unitPrice * paidQuantity,
          finalLinePrice: bundleTotal,
        };
      }

      if (item.key === bonusItem.key) {
        return {
          originalLinePrice: unitPrice * bonusQuantity,
          finalLinePrice: 0,
        };
      }

      return null;
    }

    renderItem(item, presentation) {
      const image = item.image
        ? `<img src="${item.image}" alt="${escapeHTML(item.title)}" loading="eager">`
        : '';
      const variant = item.variant_title ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
      const bundleId = item.properties?._bundle_id || '';
      const bundleRole = item.properties?._bundle_role || '';
      const bundleOffer = item.properties?._bundle_offer || '';
      const isBundle = Boolean(bundleId && bundleRole);
      const isBonus = bundleRole === 'bonus';
      const sellingPlan = item.selling_plan_allocation?.selling_plan;
      const isSubscription = Boolean(sellingPlan);
      const bundleLabels = [];

      if (isBonus) {
        bundleLabels.push(
          presentation
            ? '<span class="cart-drawer__bundle-label cart-drawer__bundle-label--bonus">FREE</span>'
            : '<span class="cart-drawer__bundle-label">Bonus bottle</span>'
        );
      } else {
        if (bundleOffer) {
          bundleLabels.push(`<span class="cart-drawer__bundle-label">${escapeHTML(bundleOffer)}</span>`);
        }

        if (isSubscription) {
          bundleLabels.push(`<span class="cart-drawer__bundle-label">(${escapeHTML(autoDeliveryLabel(sellingPlan.name))})</span>`);
        }
      }

      const bundleLabel = bundleLabels.join('');
      const quantityControl = isBundle
        ? `
            <div class="cart-drawer__bundle-quantity">
              <span>Qty ${item.quantity}${isBonus ? ` included${isSubscription ? ' each delivery' : ''}` : isSubscription ? ' each delivery' : ''}</span>
              ${
                isBonus
                  ? ''
                  : `<button type="button" class="cart-drawer__bundle-remove" data-cart-remove-bundle data-bundle-id="${escapeHTML(bundleId)}">Remove bundle</button>`
              }
            </div>
          `
        : isSubscription
          ? `
              <div class="cart-drawer__bundle-quantity">
                <span>Qty ${item.quantity} each delivery</span>
                <button type="button" class="cart-drawer__bundle-remove" data-cart-quantity="-${item.quantity}" data-line-key="${escapeHTML(item.key)}">Remove</button>
              </div>
            `
        : `
            <div class="cart-drawer__quantity">
              <button type="button" data-cart-quantity="-1" data-line-key="${escapeHTML(item.key)}" aria-label="Decrease quantity">-</button>
              <span data-cart-current-quantity>${item.quantity}</span>
              <button type="button" data-cart-quantity="1" data-line-key="${escapeHTML(item.key)}" aria-label="Increase quantity">+</button>
            </div>
          `;
      const originalLinePrice = presentation?.originalLinePrice ?? item.original_line_price;
      const finalLinePrice = presentation?.finalLinePrice ?? item.final_line_price;
      const originalPrice = originalLinePrice !== finalLinePrice
        ? `<s>${formatMoney(originalLinePrice)}</s>`
        : '';

      return `
        <article class="cart-drawer__item${isBundle ? ' cart-drawer__item--bundle' : ''}${isBonus ? ' cart-drawer__item--bonus' : ''}${isSubscription ? ' cart-drawer__item--subscription' : ''}" data-cart-line="${escapeHTML(item.key)}" data-quantity="${item.quantity}"${bundleId ? ` data-bundle-id="${escapeHTML(bundleId)}"` : ''}>
          <a class="cart-drawer__item-media" href="${item.url}">${image}</a>
          <div class="cart-drawer__item-info">
            <a href="${item.url}" class="cart-drawer__item-title">${escapeHTML(item.product_title)}</a>
            ${variant}
            ${bundleLabel}
            ${quantityControl}
          </div>
          <div class="cart-drawer__item-price">
            ${originalPrice}
            <strong>${formatMoney(finalLinePrice)}</strong>
          </div>
        </article>
      `;
    }
  }

  class MainProduct extends HTMLElement {
    connectedCallback() {
      this.productData = this.readProductData();
      this.form = this.querySelector(selectors.productForm);
      this.variantInput = this.querySelector('[data-main-product-variant]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.price = this.querySelector('[data-product-price]');
      this.comparePrice = this.querySelector('[data-product-compare-price]');
      this.addButton = this.querySelector('[data-main-atc]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.stickyBar = this.querySelector('[data-sticky-atc]');
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.dialog = this.querySelector('[data-product-lightbox]');
      this.purchaseOptions = [...this.querySelectorAll('[data-purchase-option]')];
      this.subscriptionInput = this.querySelector('[data-subscription-input]');
      this.subscriptionBox = this.querySelector('[data-subscription-box]');
      this.subscriptionSubtitle = this.querySelector('[data-subscription-subtitle]');
      this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
      this.bundlePropertyInput = this.querySelector('[data-bundle-property-input]');
      this.saleBadge = this.querySelector('[data-product-sale-badge]');

      this.bindMedia();
      this.bindOptions();
      this.bindQuantity();
      this.bindPurchaseOptions();
      this.bindLightbox();
      this.bindStickyBar();
      this.updateVariant(false);
    }

    readProductData() {
      const script = this.querySelector('[data-main-product-json]');

      if (!script) {
        return { variants: [] };
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return { variants: [] };
      }
    }

    bindMedia() {
      this.querySelectorAll('[data-media-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.selectMedia(trigger.dataset.mediaId));
      });

      this.querySelector('[data-thumb-prev]')?.addEventListener('click', () => {
        this.querySelector('[data-thumb-track]')?.scrollBy({ left: -140, behavior: 'smooth' });
      });

      this.querySelector('[data-thumb-next]')?.addEventListener('click', () => {
        this.querySelector('[data-thumb-track]')?.scrollBy({ left: 140, behavior: 'smooth' });
      });
    }

    bindOptions() {
      this.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', () => this.updateVariant(true));
      });
    }

    bindQuantity() {
      if (!this.quantityInput) {
        return;
      }

      this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const min = Number(this.quantityInput.min) || 1;
        const current = Number(this.quantityInput.value) || min;
        this.quantityInput.value = Math.max(min, current - 1);
      });

      this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const current = Number(this.quantityInput.value) || 1;
        this.quantityInput.value = current + 1;
      });

      this.quantityInput.addEventListener('change', () => {
        const min = Number(this.quantityInput.min) || 1;
        const current = Number(this.quantityInput.value) || min;
        this.quantityInput.value = Math.max(min, current);
      });
    }

    bindPurchaseOptions() {
      if (!this.purchaseOptions.length && !this.subscriptionInput) {
        return;
      }

      this.purchaseOptions.forEach((input) => {
        input.addEventListener('change', () => this.updatePurchaseOption());
      });

      this.subscriptionInput?.addEventListener('change', () => this.updatePurchaseOption());
      this.updatePurchaseOption();
    }

    selectedPurchaseOption() {
      return this.purchaseOptions.find((input) => input.checked) || this.purchaseOptions[0];
    }

    sellingPlanAllocation(option = this.selectedPurchaseOption()) {
      const variant = this.currentVariant();
      const requestedPlanId = option?.dataset.sellingPlanId || this.sellingPlanInput?.dataset.defaultSellingPlanId;

      if (requestedPlanId) {
        const allocation = variant?.selling_plan_allocations?.find(
          (candidate) => String(candidate.selling_plan_id) === String(requestedPlanId)
        );

        if (allocation) {
          return allocation;
        }
      }

      return variant?.selling_plan_allocations?.[0] || null;
    }

    currentSellingPlanId() {
      const allocation = this.sellingPlanAllocation();

      return allocation?.selling_plan_id || '';
    }

    purchaseOptionPricing(option, subscribed) {
      if (subscribed) {
        const allocation = this.sellingPlanAllocation(option);

        if (allocation) {
          const quantity = Math.max(1, Number(option.dataset.quantity) || 1);
          const paidQuantity = Math.max(1, Number(option.dataset.paidQuantity) || quantity);
          const totalPrice = Number(allocation.price || 0) * paidQuantity;
          const totalComparePrice = Number(allocation.compare_at_price || 0) * quantity;
          const effectiveUnitPrice = totalPrice / quantity;

          return {
            price: formatMoney(totalPrice),
            per: quantity > 1 ? `${formatMoney(effectiveUnitPrice)} per bottle` : '',
            compare: totalComparePrice > totalPrice ? formatMoney(totalComparePrice) : '',
          };
        }
      }

      return {
        price: subscribed
          ? option.dataset.subscriptionPriceText
          : option.dataset.oneTimePriceText || option.dataset.subscriptionPriceText,
        per: subscribed
          ? option.dataset.subscriptionPerText
          : option.dataset.oneTimePerText || option.dataset.subscriptionPerText,
        compare: subscribed
          ? option.dataset.subscriptionCompareText
          : option.dataset.oneTimeCompareText || option.dataset.subscriptionCompareText,
      };
    }

    updatePurchaseOption() {
      const option = this.selectedPurchaseOption();
      const subscribed = Boolean(this.subscriptionInput?.checked);
      const sellingPlanId = this.currentSellingPlanId();

      this.subscriptionBox?.classList.toggle('is-selected', subscribed);

      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = sellingPlanId;
        this.sellingPlanInput.disabled = !subscribed || !sellingPlanId;
      }

      if (this.subscriptionSubtitle && option) {
        const allocation = this.sellingPlanAllocation(option);
        const bonusQuantity = Math.max(0, Number(option.dataset.bonusQuantity) || 0);
        const bonusText = bonusQuantity > 0
          ? ` ${bonusQuantity === 1 ? '1 free bottle ships' : `${bonusQuantity} free bottles ship`} with your first order only.`
          : '';
        this.subscriptionSubtitle.textContent = allocation?.name
          ? `${allocation.name}.${bonusText} Cancel anytime.`
          : this.subscriptionSubtitle.textContent;
      }

      if (option && this.quantityInput) {
        this.quantityInput.value = Math.max(1, Number(option.dataset.quantity) || 1);
      }

      if (option && this.bundlePropertyInput) {
        this.bundlePropertyInput.disabled = true;
        this.bundlePropertyInput.value = '';
      }

      this.purchaseOptions.forEach((input) => {
        const card = input.nextElementSibling;

        if (!card) {
          return;
        }

        const pricing = this.purchaseOptionPricing(input, subscribed);
        const { price, per, compare } = pricing;

        const priceNode = card.querySelector('[data-purchase-price-text]');
        const perNode = card.querySelector('[data-purchase-per-text]');
        const compareNode = card.querySelector('[data-purchase-compare-text]');

        if (priceNode) {
          priceNode.textContent = price || '';
        }

        if (perNode) {
          perNode.textContent = per || '';
        }

        if (compareNode) {
          compareNode.textContent = compare || '';
          compareNode.hidden = !compare;
        }
      });

      if (option && this.price) {
        const pricing = this.purchaseOptionPricing(option, subscribed);
        const priceText = pricing.price;
        const compareText = pricing.compare;

        if (priceText) {
          this.price.textContent = priceText;
        }

        if (this.stickyPrice && priceText) {
          this.stickyPrice.textContent = priceText;
        }

        if (this.comparePrice) {
          this.comparePrice.hidden = !compareText;
          this.comparePrice.textContent = compareText || '';
        }

        if (this.saleBadge) {
          const priceAmount = parseMoneyText(priceText);
          const compareAmount = parseMoneyText(compareText);
          const staticText = this.saleBadge.dataset.staticText;

          if (priceAmount > 0 && compareAmount > priceAmount) {
            if (staticText) {
              this.saleBadge.textContent = staticText;
            } else {
              const discount = Math.round((1 - priceAmount / compareAmount) * 100);
              this.saleBadge.textContent = `${discount}% off`;
            }
            this.saleBadge.hidden = false;
          } else {
            this.saleBadge.hidden = true;
          }
        }
      }
    }

    bindLightbox() {
      this.querySelectorAll('[data-lightbox-open]').forEach((button) => {
        button.addEventListener('click', () => {
          const active = this.querySelector('[data-media].is-active img');
          const image = this.dialog?.querySelector('[data-lightbox-image]');

          if (!this.dialog || !image || !active) {
            return;
          }

          image.src = button.dataset.lightboxSrc || active.currentSrc || active.src;
          image.alt = active.alt || '';
          this.dialog.showModal();
        });
      });

      this.querySelectorAll('[data-lightbox-close]').forEach((button) => {
        button.addEventListener('click', () => this.dialog?.close());
      });
    }

    bindStickyBar() {
      if (!this.stickyBar) {
        return;
      }

      const stickyThreshold = 0.25;
      const syncStickyBar = () => {
        const scrollableHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        const progress = window.scrollY / scrollableHeight;

        this.stickyBar.classList.toggle('is-visible', progress >= stickyThreshold);
      };

      this.stickyButton?.addEventListener('click', () => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      window.addEventListener('scroll', syncStickyBar, { passive: true });
      window.addEventListener('resize', syncStickyBar);
      syncStickyBar();
    }

    selectedOptions() {
      return [...this.querySelectorAll('[data-option-position]')].map((group) => {
        return group.querySelector('[data-option-input]:checked')?.value;
      });
    }

    currentVariant() {
      const variants = this.productData.variants || [];

      if (!this.querySelector('[data-option-input]')) {
        return variants.find((variant) => String(variant.id) === this.variantInput?.value) || variants[0];
      }

      const selected = this.selectedOptions();

      return variants.find((variant) => {
        return selected.every((option, index) => variant.options[index] === option);
      });
    }

    updateVariant(pushState) {
      const variant = this.currentVariant();

      if (!variant) {
        return;
      }

      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      if (this.price) {
        this.price.textContent = this.price.dataset.subscriptionPriceText || formatMoney(variant.price);
      }

      if (this.stickyPrice) {
        this.stickyPrice.textContent = this.stickyPrice.dataset.subscriptionPriceText || formatMoney(variant.price);
      }

      if (this.comparePrice) {
        const subscriptionCompareText = this.comparePrice.dataset.subscriptionCompareText;
        const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (subscriptionCompareText) {
          this.comparePrice.hidden = false;
          this.comparePrice.textContent = subscriptionCompareText;
        } else {
          this.comparePrice.hidden = !hasComparePrice;
          this.comparePrice.textContent = hasComparePrice ? formatMoney(variant.compare_at_price) : '';
        }
      }

      if (this.addButton) {
        this.addButton.disabled = !variant.available;
        this.addButton.querySelector('[data-main-atc-text]').textContent = variant.available
          ? this.addButton.dataset.availableText
          : this.addButton.dataset.soldOutText;
      }

      if (this.stickyButton) {
        this.stickyButton.disabled = !variant.available;
      }

      this.updatePurchaseOption();

      if (variant.featured_media_id && this.querySelector(`[data-media="${variant.featured_media_id}"]`)) {
        this.selectMedia(String(variant.featured_media_id));
      }

      if (pushState && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }
    }

    selectMedia(mediaId) {
      this.querySelectorAll('[data-media]').forEach((media) => {
        const active = media.dataset.media === String(mediaId);
        media.hidden = !active;
        media.classList.toggle('is-active', active);
      });

      this.querySelectorAll('[data-media-trigger]').forEach((trigger) => {
        const active = trigger.dataset.mediaId === String(mediaId);
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }

  if (!customElements.get('main-product')) {
    customElements.define('main-product', MainProduct);
  }

  document.querySelectorAll('[data-carousel]').forEach((carousel) => {
    const scroller = getCarouselScroller(carousel);

    if (!scroller) {
      return;
    }

    carousel.querySelector('[data-carousel-prev]')?.addEventListener('click', () => {
      const firstSlide = scroller.children[0];
      const step = firstSlide?.getBoundingClientRect().width || scroller.clientWidth;
      scroller.scrollBy({ left: -(step + 22), behavior: 'smooth' });
    });

    carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => {
      const firstSlide = scroller.children[0];
      const step = firstSlide?.getBoundingClientRect().width || scroller.clientWidth;
      scroller.scrollBy({ left: step + 22, behavior: 'smooth' });
    });

    scroller.addEventListener('scroll', () => updateCarouselCounter(carousel), { passive: true });
    updateCarouselCounter(carousel);
  });

  const animateCounter = (node) => {
    if (node.dataset.counterAnimated === 'true') {
      return;
    }

    const target = Number(node.dataset.target);

    if (!Number.isFinite(target)) {
      return;
    }

    node.dataset.counterAnimated = 'true';

    const duration = 900;
    const start = performance.now();

    const tick = (time) => {
      const progress = Math.min(1, (time - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      node.textContent = Math.round(target * eased);

      if (progress < 1) {
        window.requestAnimationFrame(tick);
      }
    };

    window.requestAnimationFrame(tick);
  };

  const counters = document.querySelectorAll('[data-counter]');

  if (counters.length && 'IntersectionObserver' in window) {
    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          animateCounter(entry.target);
          counterObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );

    counters.forEach((counter) => counterObserver.observe(counter));
  } else {
    counters.forEach(animateCounter);
  }

  document.querySelectorAll('[data-popup]').forEach((popup) => {
    if (!(popup instanceof HTMLDialogElement)) {
      return;
    }

    const key = popup.dataset.popupKey || 'bio-popup';
    const delay = Number(popup.dataset.popupDelay) || 0;

    if (window.sessionStorage.getItem(key)) {
      return;
    }

    window.setTimeout(() => {
      if (!popup.open) {
        popup.showModal();
      }
    }, delay);

    popup.querySelectorAll('[data-popup-close]').forEach((close) => {
      close.addEventListener('click', () => {
        window.sessionStorage.setItem(key, 'closed');
        popup.close();
      });
    });

    popup.addEventListener('cancel', () => {
      window.sessionStorage.setItem(key, 'closed');
    });
  });

  document.querySelectorAll('[data-scroll-top]').forEach((button) => {
    const threshold = Number(button.dataset.scrollThreshold) || 500;

    const sync = () => {
      button.classList.toggle('is-visible', window.scrollY > threshold);
    };

    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    window.addEventListener('scroll', sync, { passive: true });
    sync();
  });

  document.addEventListener('click', (event) => {
    const closeTrigger = event.target.closest('[data-header-drawer-close]');

    if (!closeTrigger) {
      return;
    }

    const drawer = closeTrigger.closest('[data-header-drawer]');
    if (drawer) {
      drawer.removeAttribute('open');
    }
  });

  document.addEventListener('click', async (event) => {
    const removeButton = event.target.closest('[data-cart-page-remove-bundle]');

    if (!removeButton) {
      return;
    }

    const bundleId = removeButton.dataset.bundleId;

    if (!bundleId) {
      return;
    }

    removeButton.setAttribute('aria-busy', 'true');

    try {
      await removeBundle(bundleId);
      window.location.reload();
    } catch (error) {
      removeButton.removeAttribute('aria-busy');
      window.alert('Unable to remove this bundle. Please try again.');
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest(selectors.productForm);
    const drawer = document.querySelector(selectors.cartDrawer);

    if (!form || !drawer) {
      return;
    }

    event.preventDefault();

    const submitter = event.submitter;
    const disabledSellingPlanInputs = [];
    let bundleId = '';
    let splitBundle = false;

    if (submitter?.hasAttribute('data-buy-once-submit')) {
      form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
        if (!input.disabled) {
          input.disabled = true;
          disabledSellingPlanInputs.push(input);
        }
      });
    }

    submitter?.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData(form);
      const addUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const selectedOffer = form.querySelector('[data-purchase-option]:checked');
      const paidQuantity = Number(selectedOffer?.dataset.paidQuantity || 0);
      const bonusQuantity = Number(selectedOffer?.dataset.bonusQuantity || 0);
      const isSubscription = Boolean(formData.get('selling_plan'));
      const variantId = Number(formData.get('id'));
      splitBundle = Boolean(selectedOffer && paidQuantity > 0 && bonusQuantity > 0);
      let requestOptions;
      let updatedCart;

      if (selectedOffer && variantId) {
        await removeVariantLines(variantId);
      }

      if (splitBundle) {
        if (!variantId) {
          throw new Error('Unable to identify the selected product');
        }

        bundleId = createBundleId();

        const offer = selectedOffer.dataset.offerTitle || 'Bundle offer';
        const sellingPlanId = Number(formData.get('selling_plan')) || null;
        const sharedProperties = {
          _bundle_id: bundleId,
          _bundle_offer: offer,
          _bundle_paid_quantity: String(paidQuantity),
          _bundle_bonus_quantity: String(bonusQuantity),
        };

        requestOptions = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: paidQuantity,
                ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
                properties: { ...sharedProperties, _bundle_role: 'paid' },
              },
              {
                id: variantId,
                quantity: bonusQuantity,
                properties: { ...sharedProperties, _bundle_role: 'bonus' },
              },
            ],
          }),
        };
      } else {
        requestOptions = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: formData,
        };
      }

      const response = await fetch(addUrl, {
        ...requestOptions,
      });

      if (!response.ok) {
        throw new Error('Unable to add product to cart');
      }

      if (splitBundle) {
        updatedCart = await cartJson();

        if (!bundleDiscountApplied(updatedCart, bundleId, paidQuantity)) {
          await removeBundle(bundleId, updatedCart);
          bundleId = '';
          throw new Error('This bundle discount is temporarily unavailable. Please try again shortly.');
        }
      } else {
        updatedCart = await cartJson();
      }

      drawer.open(updatedCart);
    } catch (error) {
      if (splitBundle) {
        if (bundleId) {
          await removeBundle(bundleId).catch(() => {});
        }
        window.alert(error.message || 'Unable to add this bundle to the cart.');
      } else {
        form.submit();
      }
    } finally {
      disabledSellingPlanInputs.forEach((input) => {
        input.disabled = false;
      });
      submitter?.removeAttribute('aria-busy');
    }
  });
})();
